/**
 * useTriggerAppJob Hook
 *
 * Custom React Query mutation hook for manually triggering a appJob execution.
 * Creates a background job for immediate execution without waiting for the appJobd time.
 *
 * Note: This calls the Runtime API (not Tenant API) to maintain proper
 * security separation. The Runtime API has write access to the runtime API table
 * where background jobs are stored.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { backgroundJobService } from '../../../api/background-job.service';
import { getErrorMessage } from '../../../lib/utils/errors';

interface UseTriggerAppJobOptions {
  onSuccess?: (result: { jobId: string; message: string }) => void;
  onError?: (error: string) => void;
}

interface TriggerAppJobParams {
  appJobId: string;
  applicationId: string;
}

/**
 * Hook for triggering a appJob on-demand
 * @param tenantId - Tenant ID
 * @param options - Success/error callbacks
 */
export const useTriggerAppJob = (
  tenantId: string,
  options?: UseTriggerAppJobOptions
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ appJobId, applicationId }: TriggerAppJobParams) =>
      backgroundJobService.triggerAppJob(tenantId, applicationId, appJobId),
    onSuccess: (data) => {
      // Invalidate appJob queries to refresh lastRunAt/lastRunJobId
      queryClient.invalidateQueries({
        queryKey: ['appJobs', tenantId],
        exact: false,
      });
      queryClient.invalidateQueries({
        queryKey: ['appJob', tenantId],
        exact: false,
      });
      // Also invalidate background jobs list so the new job shows up
      queryClient.invalidateQueries({
        queryKey: ['backgroundJobs', tenantId],
        exact: false,
      });
      options?.onSuccess?.({ jobId: data.jobId, message: data.message });
    },
    onError: (error: unknown) => {
      const errorMessage = getErrorMessage(error);
      options?.onError?.(errorMessage);
    },
  });
};
